import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, Button, Row, Col, Form, Input, Switch, Modal } from "antd";
import axios from "axios";
import LogoImage from "../assets/images/header-logo.png";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import { AuthContext } from "../context/AuthContext";
import Loader from "../components/Loader";
import apiPath from "../constants/apiPath";

const { Content } = Layout;

const SignIn = () => {
  const { setIsLoggedIn, setUserProfile } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState();
  const { request } = useRequest();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Watch fields to enable/disable sign in button
  const email = Form.useWatch("email", form);
  const password = Form.useWatch("password", form);

  const isFormValid =
    email && password && email.trim() !== "" && password.trim() !== "";

  const [form1] = Form.useForm();
  const [form3] = Form.useForm();

  const getTokenFromResponse = (data) =>
    data?.access ||
    data?.access_token ||
    data?.accessToken ||
    data?.token ||
    data?.data?.access ||
    data?.data?.access_token ||
    data?.data?.accessToken ||
    data?.data?.token ||
    data?.result?.access ||
    data?.result?.access_token ||
    data?.result?.accessToken ||
    data?.result?.token;

  const getRefreshFromResponse = (data) =>
    data?.refresh ||
    data?.refresh_token ||
    data?.refreshToken ||
    data?.data?.refresh ||
    data?.data?.refresh_token ||
    data?.data?.refreshToken ||
    data?.result?.refresh ||
    data?.result?.refresh_token ||
    data?.result?.refreshToken;

  const getUserFromResponse = (data) =>
    data?.user ||
    data?.data?.user ||
    data?.result?.user ||
    data?.data?.profile ||
    data?.result?.profile ||
    data?.profile;

  // Save credentials to localStorage
  const saveCredentials = (email, password) => {
    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
      localStorage.setItem("remembered_email", email);
      // Simple encoding for password (not secure for production)
      localStorage.setItem("remembered_password", btoa(password));
    }
  };

  // Load saved credentials
  const loadSavedCredentials = () => {
    const savedRememberMe = localStorage.getItem("rememberMe");
    const savedEmail = localStorage.getItem("remembered_email");
    const savedPassword = localStorage.getItem("remembered_password");
    
    if (savedRememberMe === "true" && savedEmail && savedPassword) {
      try {
        const decodedPassword = atob(savedPassword);
        form.setFieldsValue({
          email: savedEmail,
          password: decodedPassword,
        });
        setRememberMe(true);
      } catch (error) {
        console.error("Error decoding password:", error);
        // Clear invalid data
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("remembered_email");
        localStorage.removeItem("remembered_password");
      }
    }
  };

  // Clear only session data (not remember me credentials)
  const clearSessionData = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("session_id");
    // Do NOT remove rememberMe credentials here
  };

  // ----- MARKETPLACE LOGIN -----
  const onSubmit = async (values) => {
    const { email, password } = values;
    if (!email) return ShowToast("Please enter email", Severty.ERROR);
    if (!password) return ShowToast("Please enter password", Severty.ERROR);

    setLoading(true);

    try {
      const response = await axios.post(
        `${apiPath.baseURL}${apiPath.login}`,
        { email, username: email, password },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      const data = response.data;
      setLoading(false);

      const token = getTokenFromResponse(data);
      const refreshToken = getRefreshFromResponse(data);
      const user = getUserFromResponse(data);

      if (token) {
        localStorage.setItem("token", token);
        if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
        if (data?.session_id || data?.sessionId) {
          localStorage.setItem("session_id", data?.session_id || data?.sessionId);
        }
        if (user) localStorage.setItem("user", JSON.stringify(user));

        ShowToast(data.message || "Login successful", Severty.SUCCESS);

        // Save credentials if Remember Me is checked
        if (rememberMe) {
          saveCredentials(email, password);
        }

        setIsLoggedIn(true);
        setUserProfile(user);
        navigate("/dashboard", { replace: true });
      } else {
        ShowToast(data?.message || "Login failed", Severty.ERROR);
      }
    } catch (error) {
      setLoading(false);
      const errorMsg = error.response?.data?.message || "Login error";
      ShowToast(errorMsg, Severty.ERROR);
      console.error("Login error:", error.response);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  // ----- Forgot password -----
  const handleForgotPassword = () => setVisible(true);

  const handleResetPassword = (data) => {
    const payload = { email: data.email };
    request({
      url: "/auth/forgot-password",
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setVisible(false);
        form1.resetFields();
      },
      onError: (error) => {
        ShowToast(error.response?.data?.message, Severty.ERROR);
      },
    });
  };

  // ----- Reset password -----
  const handleReset = (values) => {
    const { newPassword } = values;
    const payload = { email: selected?.email, password: newPassword };
    request({
      url: "/auth/reset-password",
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setResetModal(false);
        form3.resetFields();
      },
      onError: (error) => {
        ShowToast(error.response?.data?.message, Severty.ERROR);
      },
    });
  };

  const handleCancelReset = () => {
    setResetModal(false);
    form3.resetFields();
  };

  // Handle Remember Me change
  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      // Only clear if user explicitly unchecks
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("remembered_email");
      localStorage.removeItem("remembered_password");
    } else {
      // If checked and form has values, save them immediately
      const emailValue = form.getFieldValue("email");
      const passwordValue = form.getFieldValue("password");
      if (emailValue && passwordValue) {
        saveCredentials(emailValue, passwordValue);
      }
    }
  };

  // ----- Redirect if already logged in -----
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/dashboard", { replace: true });
    } else {
      loadSavedCredentials();
    }
  }, [navigate]);

  return (
    <>
      {loading && <Loader />}
      <Layout className="layout-default layout-signin">
        <Row className="align-items-center layout-signin-rowsss">
          <Col span={24} lg={24}>
            <Content className="signin">
              <div className="signin-box">
                <div className="signup-logo">
                  <div className="brand_logo">
                    <img src={LogoImage} alt="Logo" />
                  </div>
                  <h2>Welcome Admin Panel</h2>
                  <h4>Sign in to continue</h4>
                </div>
                <Row justify="space-around">
                  <Col xs={{ span: 24 }} lg={{ span: 24 }} md={{ span: 24 }}>
                    <div className="signup-form">
                      <Form
                        form={form}
                        onFinish={onSubmit}
                        onFinishFailed={onFinishFailed}
                        layout="vertical"
                        className="row-col"
                      >
                        <Form.Item
                          className="username"
                          label="Email"
                          name="email"
                          rules={[
                            {
                              required: true,
                              message: "Please input your email!",
                            },
                            { type: "email", message: "Invalid email format" },
                            {
                              max: 100,
                              message: "Email cannot exceed 100 characters",
                            },
                          ]}
                        >
                          <Input 
                            placeholder="Email" 
                            onChange={() => {
                              // If remember me is on and both fields have values, save
                              if (rememberMe && form.getFieldValue("email") && form.getFieldValue("password")) {
                                saveCredentials(
                                  form.getFieldValue("email"),
                                  form.getFieldValue("password")
                                );
                              }
                            }}
                          />
                        </Form.Item>

                        <Form.Item
                          className="username"
                          label="Password"
                          name="password"
                          rules={[
                            {
                              required: true,
                              message: "Please input your password!",
                            },
                            {
                              max: 100,
                              message: "Password cannot exceed 100 characters",
                            },
                          ]}
                        >
                          <Input.Password 
                            placeholder="Password"
                            onChange={() => {
                              // If remember me is on and both fields have values, save
                              if (rememberMe && form.getFieldValue("email") && form.getFieldValue("password")) {
                                saveCredentials(
                                  form.getFieldValue("email"),
                                  form.getFieldValue("password")
                                );
                              }
                            }}
                          />
                        </Form.Item>

                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: 24
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Switch
                              checked={rememberMe}
                              onChange={handleRememberMeChange}
                              size="default"
                            />
                            <span style={{ marginLeft: 8 }}>Remember me</span>
                          </div>
                          {/* <div
                            className="forgate-btn"
                            onClick={handleForgotPassword}
                            style={{ cursor: 'pointer' }}
                          >
                            <a>Forgot Password?</a>
                          </div> */}
                        </div>

                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            style={{ width: "100%" }}
                            disabled={!isFormValid}
                            loading={loading}
                          >
                            SIGN IN
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </Col>
                </Row>
              </div>
            </Content>
          </Col>
        </Row>

        {/* Forgot Password Modal */}
        <Modal
          open={visible}
          title="Forgot Password"
          okText="Send Reset Link"
          onCancel={() => {
            setVisible(false);
            form1.resetFields();
          }}
          okButtonProps={{
            form: "forget-password",
            htmlType: "submit",
            loading: loading,
          }}
        >
          <Form
            id="forget-password"
            form={form1}
            onFinish={handleResetPassword}
            layout="vertical"
          >
            <Form.Item
              label="Registered Email"
              name="email"
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Invalid email format" },
              ]}
            >
              <Input placeholder="Enter your registered email" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Reset Password Modal */}
        <Modal
          open={resetModal}
          title="Reset Password"
          okText="Reset"
          onCancel={handleCancelReset}
          okButtonProps={{
            form: "reset-password",
            htmlType: "submit",
            loading: loading,
          }}
        >
          <Form
            id="reset-password"
            form={form3}
            onFinish={handleReset}
            layout="vertical"
          >
            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[
                { required: true, message: "Please input the new password!" },
                { min: 8, message: "Password must be at least 8 characters" },
                {
                  pattern:
                    /^(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z0-9!@#$%^&*(),.?":{}|<>]+$/,
                  message: "Must include at least one special character",
                },
                {
                  pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[a-z]).+$/,
                  message: "Must contain uppercase, lowercase, and number",
                },
                { max: 100, message: "Password too long" },
              ]}
            >
              <Input.Password placeholder="New password" />
            </Form.Item>
            <Form.Item
              label="Confirm New Password"
              name="confirm_new_password"
              dependencies={["newPassword"]}
              hasFeedback
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match!"));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm new password" />
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </>
  );
};

export default SignIn;